/**
 * Cryptographic randomness (Web Crypto) for retry jitter and non-guessable IDs.
 * Avoids `Math.random` (Sonar S2245 / pseudorandom unsuitable for security).
 */

const UINT32_RANGE = 4294967296; // 2**32 (avoids inconsistent hex separators for linters)

/** Uniform value in [0, 1) from 32 bits; 0 if `crypto.getRandomValues` is unavailable. */
export function randomUnit01(): number {
  const c = globalThis.crypto;
  if (typeof c?.getRandomValues !== 'function') return 0;
  const buf = new Uint32Array(1);
  c.getRandomValues(buf);
  return buf[0] / UINT32_RANGE;
}

/** Lowercase hex of `byteLength` bytes; empty string if crypto is unavailable. */
function randomHex(byteLength: number): string {
  const c = globalThis.crypto;
  if (typeof c?.getRandomValues !== 'function' || byteLength <= 0) return '';
  const buf = new Uint8Array(byteLength);
  c.getRandomValues(buf);
  let s = '';
  for (const byte of buf) {
    s += byte.toString(16).padStart(2, '0');
  }
  return s;
}

/**
 * Prefixed opaque id for local rows (delivery points, redemption headers/lines on insert, PEP participant lines,
 * acudiente, archived files, bonos, PDF drafts).
 * Offline sync payloads carry these `publicId` values (`pe_…`, `rb_…`, `rbp_…`) as in server integration examples.
 */
export function newPrefixedPublicId(prefix: string): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return `${prefix}_${c.randomUUID()}`;
  }
  const h = randomHex(16);
  return h ? `${prefix}_${Date.now()}_${h}` : `${prefix}_${Date.now()}`;
}
